/**
 * InspectionEditModal (Plan 04-04 Task 2) — PM web admin surface.
 *
 * Mirrors the Android InspectionFormScreen's Tab 1 + Tab 2 + applied-coating
 * conditional reveal so spec parity is observable across surfaces. PM Save
 * sends only the diff via PATCH /api/admin/inspections/{id}; on success the
 * modal closes and the asset GeoJSON query invalidates (drives the map
 * recolor within ≤200ms per D-87 budget).
 *
 * Anatomy follows NewWorkerModal:
 *   <dialog ref={dialogRef} onCancel={preventDefault}>
 *     <form onSubmit={handleSubmit}>
 *       <fieldset>Tab 1 — 4 booleans + coating select</fieldset>
 *       <fieldset>Tab 2 — 3 booleans + conditional applied-type picker</fieldset>
 *       <Banner variant="error" />?  (when mutation.isError)
 *       <Cancel/Save row>
 *     </form>
 *   </dialog>
 *
 * Tailwind logical utilities throughout (Phase 1 D-11 + W5 hygiene):
 *   ps-* / pe- / pbs- / pbe- / mbs- / mbe-  — never pl-/pr-/pt-/pb-/mt-/mb-.
 *
 * i18n codes consumed (243-entry catalog after Plan 04-04 RED):
 *   LABEL_INSPECTION_EDIT_HEADING + LABEL_TAB_DAILY_CHECKUP +
 *   LABEL_TAB_REPAIRS + LABEL_DIMENSION_CLEANING + LABEL_CLEAN +
 *   LABEL_NOT_CLEAN (R3-005/D4 forced-choice radio) +
 *   LABEL_CHECKBOX_STRUCTURE_DAMAGED +
 *   LABEL_CHECKBOX_COVER_NEEDS_LEVEL_ADJUSTMENT +
 *   LABEL_CHECKBOX_PIPE_BLOCKED + LABEL_COATING_TITLE +
 *   LABEL_COATING_NONE + LABEL_COATING_BITUMEN + LABEL_COATING_EPOXY +
 *   LABEL_CHECKBOX_ADJUSTED_COVER_LEVEL +
 *   LABEL_CHECKBOX_REPAIRED_INTERNAL_STRUCTURE +
 *   LABEL_CHECKBOX_APPLIED_COATING + LABEL_APPLIED_TYPE_REQUIRED +
 *   LABEL_PM_EDIT_COUNT + LABEL_CANCEL + LABEL_SAVE +
 *   ERR_INSPECTION_APPLIED_COATING_REQUIRES_TYPE +
 *   ERR_INSPECTION_NOT_FOUND + ERR_GENERIC.
 */
import {
  type FormEvent,
  type SyntheticEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Pencil } from "lucide-react";
import type {
  CoatingValue,
  InspectionEditPayload,
  InspectionRead,
  ObservedCoatingValue,
} from "../../api/inspections";
import { useInspectionEdit } from "./useInspectionEdit";
import { Button } from "../../components/Button";
import { Select } from "../../components/Select";
import { Banner } from "../../components/Banner";

interface InspectionEditModalProps {
  inspection: InspectionRead;
  onClose: () => void;
}

interface FormFields {
  tab1_cleaned: boolean;
  tab1_structure_damaged: boolean;
  tab1_cover_needs_level_adjustment: boolean;
  tab1_pipe_blocked: boolean;
  tab1_coating: ObservedCoatingValue;
  tab2_adjusted_cover_level: boolean;
  tab2_repaired_internal_structure: boolean;
  tab2_applied_coating: boolean;
  tab2_applied_coating_type: CoatingValue;
}

function fieldsFromInspection(insp: InspectionRead): FormFields {
  return {
    tab1_cleaned: insp.tab1_cleaned,
    tab1_structure_damaged: insp.tab1_structure_damaged,
    tab1_cover_needs_level_adjustment: insp.tab1_cover_needs_level_adjustment,
    tab1_pipe_blocked: insp.tab1_pipe_blocked,
    tab1_coating: insp.tab1_coating,
    tab2_adjusted_cover_level: insp.tab2_adjusted_cover_level,
    tab2_repaired_internal_structure: insp.tab2_repaired_internal_structure,
    tab2_applied_coating: insp.tab2_applied_coating,
    tab2_applied_coating_type: insp.tab2_applied_coating_type,
  };
}

/** Diff helper: only the keys whose value differs from the original. */
function diffPayload(
  initial: FormFields,
  current: FormFields,
): InspectionEditPayload {
  const out: InspectionEditPayload = {};
  (Object.keys(current) as (keyof FormFields)[]).forEach((k) => {
    if (current[k] !== initial[k]) {
      // TS: assignment is structurally safe — keys are a subset of
      // InspectionEditPayload and the types match.
      (out as Record<string, FormFields[keyof FormFields]>)[k] = current[k];
    }
  });
  return out;
}

/** Save disabled iff applied_coating ticked but type still null. */
function canSave(fields: FormFields): boolean {
  if (fields.tab2_applied_coating && fields.tab2_applied_coating_type === null) {
    return false;
  }
  return true;
}

export function InspectionEditModal({
  inspection,
  onClose,
}: InspectionEditModalProps) {
  const intl = useIntl();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const initialRef = useRef<FormFields>(fieldsFromInspection(inspection));
  const [fields, setFields] = useState<FormFields>(initialRef.current);

  const editMutation = useInspectionEdit(inspection.id, inspection.asset_id);

  useEffect(() => {
    dialogRef.current?.showModal();
    return () => {
      dialogRef.current?.close();
    };
  }, []);

  const handleClose = () => {
    onClose();
  };

  const handleDialogCancel = (e: SyntheticEvent) => {
    e.preventDefault();
    handleClose();
  };

  const handleAppliedCoatingToggle = (checked: boolean) => {
    setFields((prev) => ({
      ...prev,
      tab2_applied_coating: checked,
      // When unticking, clear the type so the diff is clean.
      tab2_applied_coating_type: checked ? prev.tab2_applied_coating_type : null,
    }));
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!canSave(fields)) return;
    const payload = diffPayload(initialRef.current, fields);
    if (Object.keys(payload).length === 0) {
      // No-op save — just close.
      onClose();
      return;
    }
    editMutation.mutate(payload, {
      onSuccess: () => {
        onClose();
      },
    });
  };

  const errorCode = editMutation.isError
    ? (editMutation.error?.code ?? "ERR_GENERIC")
    : null;

  return (
    <dialog
      ref={dialogRef}
      onCancel={handleDialogCancel}
      data-testid="inspection-edit-modal"
      className="rounded-lg border border-divider bg-surface-raised shadow-lg p-0 backdrop:bg-black/40 inline-size-[640px] max-inline-size-[calc(100vw-2rem)]"
    >
      <div className="ps-6 pe-6 pbs-6 pbe-6">
        <div className="flex items-start justify-between mbe-4">
          <h2 className="text-xl font-semibold text-on-surface">
            <FormattedMessage id="LABEL_INSPECTION_EDIT_HEADING" />
          </h2>
          {inspection.pm_edit_count > 0 && (
            <span
              data-testid="inspection-edit-pm-count"
              className="inline-flex items-center gap-1 text-sm text-warning"
            >
              <Pencil
                size={14}
                aria-label={intl.formatMessage({ id: "LABEL_PENCIL_ICON_DESC" })}
              />
              <FormattedMessage
                id="LABEL_PM_EDIT_COUNT"
                values={{ count: inspection.pm_edit_count }}
              />
            </span>
          )}
        </div>

        {errorCode && (
          <div className="mbe-4">
            <Banner variant="error" data-testid="inspection-edit-error-banner">
              <FormattedMessage id={errorCode} />
            </Banner>
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          <div className="flex flex-col gap-6">
            {/* ── Tab 1 — Daily check-up ──────────────────────────────── */}
            <fieldset className="border border-divider rounded-md ps-4 pe-4 pbs-3 pbe-4">
              <legend className="text-sm font-semibold text-on-surface-variant ps-1 pe-1">
                <FormattedMessage id="LABEL_TAB_DAILY_CHECKUP" />
              </legend>
              <div className="flex flex-col gap-2">
                {/* R3-005/D4 (audit 2026-07) — forced-choice Clean / Not clean
                    radio, mirroring Android's Tab-1 cleaning control.
                    tab1_cleaned=false is the "Not clean" verdict that turns
                    the pin red (cleaning_dirty_asserted); as a checkbox in a
                    column of checkboxes it read as "nothing to flag", so a PM
                    unticking it while correcting an inspection reddened the
                    asset unknowingly. The radio makes false an explicit pick,
                    so it means the same thing on both surfaces. */}
                <div
                  role="radiogroup"
                  aria-label={intl.formatMessage({ id: "LABEL_DIMENSION_CLEANING" })}
                  className="flex items-center gap-3"
                >
                  <span className="text-base text-on-surface">
                    <FormattedMessage id="LABEL_DIMENSION_CLEANING" />
                  </span>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="inspection-edit-cleaning-verdict"
                      data-testid="inspection-edit-tab1-cleaned"
                      checked={fields.tab1_cleaned}
                      onChange={() => setFields((p) => ({ ...p, tab1_cleaned: true }))}
                      className="h-4 w-4 border-divider text-primary focus-visible:ring-primary"
                    />
                    <span className="text-base text-on-surface">
                      <FormattedMessage id="LABEL_CLEAN" />
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="inspection-edit-cleaning-verdict"
                      data-testid="inspection-edit-tab1-not-clean"
                      checked={!fields.tab1_cleaned}
                      onChange={() => setFields((p) => ({ ...p, tab1_cleaned: false }))}
                      className="h-4 w-4 border-divider text-error focus-visible:ring-primary"
                    />
                    <span className="text-base text-on-surface">
                      <FormattedMessage id="LABEL_NOT_CLEAN" />
                    </span>
                  </label>
                </div>
                <BooleanRow
                  testId="inspection-edit-tab1-structure-damaged"
                  labelId="LABEL_CHECKBOX_STRUCTURE_DAMAGED"
                  checked={fields.tab1_structure_damaged}
                  onChange={(v) =>
                    setFields((p) => ({ ...p, tab1_structure_damaged: v }))
                  }
                />
                <BooleanRow
                  testId="inspection-edit-tab1-cover-needs-level-adjustment"
                  labelId="LABEL_CHECKBOX_COVER_NEEDS_LEVEL_ADJUSTMENT"
                  checked={fields.tab1_cover_needs_level_adjustment}
                  onChange={(v) =>
                    setFields((p) => ({
                      ...p,
                      tab1_cover_needs_level_adjustment: v,
                    }))
                  }
                />
                <BooleanRow
                  testId="inspection-edit-tab1-pipe-blocked"
                  labelId="LABEL_CHECKBOX_PIPE_BLOCKED"
                  checked={fields.tab1_pipe_blocked}
                  onChange={(v) =>
                    setFields((p) => ({ ...p, tab1_pipe_blocked: v }))
                  }
                />
                <div className="mbs-2">
                  <Select
                    id="inspection-edit-tab1-coating"
                    label={intl.formatMessage({ id: "LABEL_COATING_TITLE" })}
                    // R3-003/D3 — a legacy NULL displays as None; the field
                    // state stays null until the PM actively changes it, so
                    // the PATCH diff never fabricates a 'none' observation.
                    value={fields.tab1_coating ?? "none"}
                    onChange={(v) =>
                      setFields((p) => ({
                        ...p,
                        tab1_coating: v as "bitumen" | "epoxy" | "none",
                      }))
                    }
                    options={[
                      {
                        value: "none",
                        label: intl.formatMessage({ id: "LABEL_COATING_NONE" }),
                      },
                      {
                        value: "bitumen",
                        label: intl.formatMessage({ id: "LABEL_COATING_BITUMEN" }),
                      },
                      {
                        value: "epoxy",
                        label: intl.formatMessage({ id: "LABEL_COATING_EPOXY" }),
                      },
                    ]}
                    data-testid="inspection-edit-tab1-coating-select"
                  />
                </div>
              </div>
            </fieldset>

            {/* ── Tab 2 — Repairs ─────────────────────────────────────── */}
            <fieldset className="border border-divider rounded-md ps-4 pe-4 pbs-3 pbe-4">
              <legend className="text-sm font-semibold text-on-surface-variant ps-1 pe-1">
                <FormattedMessage id="LABEL_TAB_REPAIRS" />
              </legend>
              <div className="flex flex-col gap-2">
                <BooleanRow
                  testId="inspection-edit-tab2-adjusted-cover-level"
                  labelId="LABEL_CHECKBOX_ADJUSTED_COVER_LEVEL"
                  checked={fields.tab2_adjusted_cover_level}
                  onChange={(v) =>
                    setFields((p) => ({ ...p, tab2_adjusted_cover_level: v }))
                  }
                />
                <BooleanRow
                  testId="inspection-edit-tab2-repaired-internal-structure"
                  labelId="LABEL_CHECKBOX_REPAIRED_INTERNAL_STRUCTURE"
                  checked={fields.tab2_repaired_internal_structure}
                  onChange={(v) =>
                    setFields((p) => ({
                      ...p,
                      tab2_repaired_internal_structure: v,
                    }))
                  }
                />
                <BooleanRow
                  testId="inspection-edit-tab2-applied-coating"
                  labelId="LABEL_CHECKBOX_APPLIED_COATING"
                  checked={fields.tab2_applied_coating}
                  onChange={handleAppliedCoatingToggle}
                />
                {fields.tab2_applied_coating && (
                  <div className="mbs-2">
                    <Select
                      id="inspection-edit-tab2-applied-coating-type"
                      label={intl.formatMessage({
                        id: "LABEL_APPLIED_TYPE_REQUIRED",
                      })}
                      value={fields.tab2_applied_coating_type ?? ""}
                      onChange={(v) =>
                        setFields((p) => ({
                          ...p,
                          tab2_applied_coating_type:
                            v === "" ? null : (v as "bitumen" | "epoxy"),
                        }))
                      }
                      options={[
                        {
                          value: "",
                          label: intl.formatMessage({
                            id: "LABEL_APPLIED_TYPE_REQUIRED",
                          }),
                        },
                        {
                          value: "bitumen",
                          label: intl.formatMessage({
                            id: "LABEL_COATING_BITUMEN",
                          }),
                        },
                        {
                          value: "epoxy",
                          label: intl.formatMessage({
                            id: "LABEL_COATING_EPOXY",
                          }),
                        },
                      ]}
                      data-testid="inspection-edit-tab2-applied-coating-type-select"
                    />
                  </div>
                )}
              </div>
            </fieldset>
          </div>

          <div className="flex justify-end gap-3 mbs-8">
            <Button
              variant="secondary"
              type="button"
              onClick={handleClose}
              disabled={editMutation.isPending}
              data-testid="inspection-edit-cancel"
            >
              <FormattedMessage id="LABEL_CANCEL" />
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={editMutation.isPending || !canSave(fields)}
              data-testid="inspection-edit-save"
            >
              <FormattedMessage id="LABEL_SAVE" />
            </Button>
          </div>
        </form>
      </div>
    </dialog>
  );
}

interface BooleanRowProps {
  testId: string;
  labelId: string;
  checked: boolean;
   
  onChange: (next: boolean) => void;
}

function BooleanRow({ testId, labelId, checked, onChange }: BooleanRowProps) {
  return (
    <label className="flex items-center gap-3 cursor-pointer">
      <input
        type="checkbox"
        data-testid={testId}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 rounded border-divider text-primary focus-visible:ring-primary"
      />
      <span className="text-base text-on-surface">
        <FormattedMessage id={labelId} />
      </span>
    </label>
  );
}
